import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { examAudio } from './exam-audio';

/**
 * The listening recording, which plays once.
 *
 * A centre holds its audio one of two ways: a single continuous file for the
 * paper, or one file per part. Which it is says nothing about the exam, so it
 * is resolved here — the tracks play back to back, and the candidate cannot
 * tell the difference. A single file is simply a list of one.
 *
 * The real test gives a candidate no transport at all — no pause, no rewind,
 * no scrub — only volume. That is enforced rather than merely hidden: the
 * element has no controls, and any attempt to move the playhead (a media key,
 * a browser gesture, an extension) is undone by snapping it back to where the
 * server says the recording should be.
 *
 * That expected position is derived from the offset the server supplied at
 * load plus the wall time since, so a candidate who refreshes rejoins the
 * recording where it actually is instead of starting it again — and with
 * several tracks, on the track it actually reached.
 */

export interface ListeningHandle {
  play: () => Promise<void>;
  /** Fetches the recording again and rejoins it where it should be by now. */
  retry: () => void;
  setVolume: (v: number) => void;
}

export interface Track {
  url: string;
  durationMs: number | null;
}

interface Props {
  tracks: Track[];
  /** Where the recording should already be, per the server, when this mounts. */
  startOffsetMs: number;
  volume: number;
  /** The browser refused to start sound without a click. */
  onBlocked: () => void;
  /**
   * The recording would not play even after trying again — a file that is
   * damaged, cut short, missing, or in a format this browser cannot read.
   * Told apart from onBlocked, because a click cannot fix it.
   */
  onFailed?: (reason: string) => void;
  onEnded?: () => void;
  /** Fired the first time sound is actually coming out. */
  onPlaying?: () => void;
}

/** How far the playhead may drift before it is corrected. */
const DRIFT_TOLERANCE_S = 1.5;

/** Sound that has not moved for this long, while it should be playing, has stalled. */
const STALL_MS = 8000;

/** How many times a failing recording is fetched again before the candidate is told. */
const MAX_RETRIES = 3;

/** What a media error means, in words a candidate can pass on to a teacher. */
function describe(error: MediaError | null): string {
  switch (error?.code) {
    case 2:
      return 'the connection dropped while it was loading';
    case 3:
      return 'the recording file is damaged';
    case 4:
      return 'this browser cannot play the recording, or it could not be found';
    default:
      return 'it stopped part-way through';
  }
}

/**
 * Which track a given elapsed time falls in, and how far into it.
 *
 * A track whose length the server could not read cannot be skipped past on
 * time alone, so it stops the walk: playback falls back to the start of it and
 * carries on from there. That is the same position the candidate would be in
 * had they simply been listening.
 */
export function locate(tracks: Track[], elapsedMs: number): { index: number; offsetMs: number } {
  let remaining = Math.max(0, elapsedMs);
  for (let i = 0; i < tracks.length; i += 1) {
    const length = tracks[i]!.durationMs;
    if (length == null) return { index: i, offsetMs: 0 };
    if (remaining < length) return { index: i, offsetMs: remaining };
    remaining -= length;
  }
  // Past the end of everything: hold at the end of the last track.
  const last = Math.max(0, tracks.length - 1);
  return { index: last, offsetMs: tracks[last]?.durationMs ?? 0 };
}

export const ListeningAudio = forwardRef<ListeningHandle, Props>(function ListeningAudio(
  { tracks, startOffsetMs, volume, onBlocked, onFailed, onEnded, onPlaying: onPlayingProp },
  ref,
) {
  // The element the opening screen's clicks unlocked; see exam-audio.ts.
  const audio = useRef<HTMLAudioElement>(examAudio());
  const mountedAt = useRef(Date.now());
  const startedAtOffset = useRef(startOffsetMs);
  const started = useRef(false);

  /**
   * Whether the clock has been anchored to real playback yet.
   *
   * On a fresh sitting the server hands over an offset of zero, meaning the
   * recording has not begun. The seconds spent fetching and buffering the
   * first file are therefore not seconds of the recording, and counting them
   * would open it already that far in — the candidate would miss the opening
   * of Part 1. So on a fresh start the clock is re-anchored the instant sound
   * actually begins.
   *
   * A resume is the opposite case: the recording has been playing without this
   * browser, the server's offset is the truth, and buffering time is time the
   * candidate really has lost. There the clock is left exactly as given.
   */
  const anchored = useRef(startOffsetMs > 0);

  const [index, setIndex] = useState(() => locate(tracks, startOffsetMs).index);

  /**
   * The callbacks, read through a ref.
   *
   * The exam screen passes them inline and redraws four times a second for
   * its clock, so as effect dependencies they re-ran the listeners' effect on
   * every redraw — and its two-second drift check was cleared and set again
   * each time, so it never once ran. Read from a ref, the effect runs when the
   * track changes and not otherwise.
   */
  const callbacks = useRef({ onBlocked, onFailed, onEnded, onPlaying: onPlayingProp });
  callbacks.current = { onBlocked, onFailed, onEnded, onPlaying: onPlayingProp };

  const retries = useRef(0);
  /** Waiting on the candidate's click after a refusal: not a fault, and not to be retried. */
  const awaitingClick = useRef(false);
  const progress = useRef({ time: 0, at: Date.now() });

  /**
   * Fetches the recording again and lets loadedmetadata put it back where it
   * should be — or, past the last try, says it cannot be played.
   */
  const recover = (el: HTMLAudioElement, reason: string) => {
    if (!started.current) return;
    if (retries.current >= MAX_RETRIES) {
      callbacks.current.onFailed?.(reason);
      return;
    }
    retries.current += 1;
    progress.current = { time: el.currentTime, at: Date.now() };
    window.setTimeout(() => {
      if (audio.current === el) el.load();
    }, 1500);
  };

  /**
   * Every play goes through here, so a refusal and a fault are told apart.
   * Only a refusal is the browser "blocking the sound"; an interrupted play is
   * a newer load taking over and is nothing; anything else is the recording
   * failing, and is tried again before anybody is told.
   */
  const tryPlay = (el: HTMLAudioElement) =>
    el.play().catch((err: unknown) => {
      const name = (err as { name?: string } | null)?.name;
      if (name === 'NotAllowedError') {
        awaitingClick.current = true;
        callbacks.current.onBlocked();
      }
      else if (name !== 'AbortError') recover(el, describe(el.error));
    });

  /** Where the whole recording should be by now, across every track. */
  const elapsed = () => startedAtOffset.current + (Date.now() - mountedAt.current);

  useImperativeHandle(ref, () => ({
    async play() {
      const el = audio.current;
      if (!el) return;
      started.current = true;
      awaitingClick.current = false;
      // A refusal shows the caller's prompt; the clock is the server's either
      // way, so nothing has been lost yet.
      await tryPlay(el);
    },
    retry() {
      const el = audio.current;
      if (!el) return;
      started.current = true;
      retries.current = 0;
      progress.current = { time: el.currentTime, at: Date.now() };
      el.load();
    },
    setVolume(v: number) {
      if (audio.current) audio.current.volume = v;
    },
  }));

  /**
   * The track's source, moved onto the one element. Declared before the
   * listeners below, so they are on before anything it loads can arrive.
   */
  const url = tracks[index]?.url;
  useEffect(() => {
    const el = audio.current;
    if (!el || !url) return;
    if (el.getAttribute('src') !== url) el.src = url;
  }, [url]);

  // Leaving the paper stops the sound and lets the file go.
  useEffect(() => {
    const el = audio.current;
    return () => {
      el?.pause();
      el?.removeAttribute('src');
      el?.load();
    };
  }, []);

  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    el.volume = volume;
  }, [volume, index]);

  useEffect(() => {
    const el = audio.current;
    if (!el) return;

    const place = () => {
      /*
       * A stall: sound that should be moving and is not, with no error to say
       * why — a file cut short on the server does exactly this. Fetched again
       * rather than left silent, and only while the device is online.
       */
      if (started.current && !el.ended && !awaitingClick.current) {
        // Paused with nobody asking it to be counts too: a source that broke
        // before a sound came out leaves the element paused, silent, at 0:00.
        if (el.currentTime !== progress.current.time) {
          progress.current = { time: el.currentTime, at: Date.now() };
        } else if (Date.now() - progress.current.at > STALL_MS && navigator.onLine) {
          recover(el, 'it stopped part-way through');
          return;
        }
      }

      const here = locate(tracks, elapsed());
      if (here.index !== index) {
        setIndex(here.index);
        return;
      }
      const want = here.offsetMs / 1000;
      if (!Number.isFinite(el.duration)) return;
      if (want >= el.duration) return;
      if (Math.abs(el.currentTime - want) > DRIFT_TOLERANCE_S) {
        el.currentTime = Math.min(want, el.duration);
      }
    };

    const onPlaying = () => {
      // Reported every time, not only the first: the screen says whether
      // sound is coming out, and a claim that it is while a browser has
      // blocked it is worse than saying nothing.
      retries.current = 0;
      awaitingClick.current = false;
      callbacks.current.onPlaying?.();
      if (anchored.current) return;
      anchored.current = true;
      mountedAt.current = Date.now();
      startedAtOffset.current = 0;
      el.currentTime = 0;
    };

    const onLoaded = () => {
      const here = locate(tracks, elapsed());
      const target = here.index === index ? here.offsetMs / 1000 : 0;
      if (target > 0 && Number.isFinite(el.duration)) {
        el.currentTime = Math.min(target, el.duration);
      }
      // A later track only exists because an earlier one finished, so it plays
      // the moment it is ready rather than waiting to be asked again.
      if (started.current) void tryPlay(el);
    };

    const onTrackEnded = () => {
      if (index < tracks.length - 1) {
        setIndex(index + 1);
        return;
      }
      callbacks.current.onEnded?.();
    };

    // A paused recording is not a thing the real exam has; resume immediately.
    const onPause = () => {
      if (!el.ended && started.current) void tryPlay(el);
    };

    const onError = () => recover(el, describe(el.error));

    el.addEventListener('playing', onPlaying);
    el.addEventListener('loadedmetadata', onLoaded);
    el.addEventListener('seeked', place);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onTrackEnded);
    el.addEventListener('error', onError);

    const id = setInterval(place, 2000);
    return () => {
      el.removeEventListener('playing', onPlaying);
      el.removeEventListener('loadedmetadata', onLoaded);
      el.removeEventListener('seeked', place);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onTrackEnded);
      el.removeEventListener('error', onError);
      clearInterval(id);
    };
    // Callbacks are read through their ref; see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, tracks]);

  /*
   * Nothing drawn. The element is not in the page at all: it was made — and
   * unlocked by the candidate's click — before the paper existed, and it
   * plays one track after another for the whole recording, so a strict
   * browser that let the first part play lets the rest. No controls, as the
   * real test has none; see the note at the top.
   */
  return null;
});
